import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Edit3Icon, Eye } from 'lucide-react';
import { useCradSize } from '@/hooks/use-card-size';
import { Image } from '@/components/common/image';
import { TruncatedText } from '@/components/common/truncated-text';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { IconButton } from '@/components/common/icon-button';
import { TrashIcon } from '@/components/ui/icon';
import { DeleteConfirmModal, Tooltip } from "@/components/ui/modal";
import { TemplateDetailModal } from '@/components/modal-template-detail';
import { agentTemplateHooks } from '@/manager';
import { PUSH_SETTINGS_TYPE, SettingTab } from '@/manager/manager/agent-dashboard-user-management-manager';
import { AddTemplateIcon, TemplateEmptyIcon } from './assets';
import { LarkImageTableItemsData } from '@/services/agent-v2-feishu-tuwen-table-data';
import { TemplateImageAddModal } from './template-image-add-modal';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { AdminPostTemplateModal } from '../agent-dashboard-company-asset/admin-post-template-modal';
import { AdminPostTemplateListItemData } from '@/services/admin-post-template-list';
import { useParams } from 'react-router-dom';

// 骨架屏容器组件
function Skeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="w-full h-full relative">
          <div className="w-full aspect-[9/12] bg-gray-300 rounded-[12px] mb-[10px] animate-pulse"></div>
          <div className="absolute bottom-1 left-[10px] w-full text-start translate-y-full">
            <div className="h-4 bg-gray-300 rounded w-3/4 animate-pulse"></div>
            <div className="h-3 bg-gray-300 rounded w-2/4 mt-1 animate-pulse"></div>
          </div>
        </div>
      ))}
    </>
  );
}

interface TemplateImageProps {
  actors: Actor[];
  voices: Audio[];
  activeTab: SettingTab;
}

/** 图文-模板 */
export const TemplateImage = ({ actors, voices, activeTab }: TemplateImageProps) => {
  const { t } = useTranslation();
  const { ref, columns } = useCradSize({ min: 170, max: 220, gap: 3 });
  const { id: agentId } = useParams();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [triggerId, setTriggerId] = useState("");

  // 模板相关
  const { templateList, loading, search } = agentTemplateHooks.useTemplateListData();
  const { loading: saveLoading, save } = agentTemplateHooks.useSaveAgentTemplate();
  const { loading: deleteLoading, deleteTemplate } = agentTemplateHooks.useDeleteAgentTemplate();
  const { loading: detailLoading, templateDetail, fetchDetail, resetDetail } = agentTemplateHooks.useTemplateDetail();

  // 打开删除确认弹窗
  const handleDelete = (uuid: string) => {
    setDeleteModalOpen(true);
    setDeleteId(uuid);
  }

  // 确认删除模板
  const confirmDelete = async () => {
    await deleteTemplate(deleteId);
    setDeleteModalOpen(false);
  }

  // 根据 type 搜索模板
  const handleSearch = async () => {
    await search(1, PUSH_SETTINGS_TYPE[activeTab]);
  }

  useEffect(() => {
    handleSearch();
  }, [activeTab])

  // 查看模板详情
  const handleDetail = async (uuid: string) => {
    try {
      setTriggerId(uuid);
      await fetchDetail(uuid);
      setDetailModalOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setTriggerId("");
    }
  }

  // 保存模板（后台图文模板列表）
  const handleSave = async (templates: AdminPostTemplateListItemData[]) => {
    const params = templates.map((item) => ({
      styleId: item.templateId,
      templateName: item.templateName,
      coverUrl: item.coverUrl,
      templateId: item.templateId,
    }));
    await save(params);
  }

  return (
    <div className="w-full h-full flex flex-col relative">
      <div className='flex items-center justify-between text-base font-medium text-[rgba(0,0,0,1)] mb-5'>
        <span>{t("agent_dashboard.user_detail.template.title")}</span>
        <AdminPostTemplateModal
          agentId={agentId}
          showPresetTag
          trigger={(
            <button className='h-10 flex items-center justify-center py-[10px] px-[14px] border border-[rgba(213,215,218,1)] hover:bg-gray-50  rounded-md shadow-sm transition-all'>
              <AddTemplateIcon />
              <span className='ml-1.5 text-sm text-[rgba(16,24,40,1)] font-semibold'>{t("agent_dashboard.user_detail.template.add_template")}</span>
            </button>
          )}
          saving={saveLoading}
          onSave={handleSave}
        />
      </div>
      <div
        ref={ref}
        className='w-full grid gap-x-2 gap-y-18'
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {loading && <Skeleton />}
        {!loading && templateList.length > 0 ? templateList?.map((item, index) => (
          <div key={item.id} className="w-full h-full relative">
            <div
              className="group relative w-full aspect-[9/12] bg-gray-300 rounded-[12px] mb-[10px] hover:shadow-lg transition-all cursor-pointer select-none"
              onClick={() => handleDetail(item.uuid)}
            >
              <div className='overflow-hidden flex-shrink-0 relative'>
                <Image src={item.coverUrl} className="size-full rounded-[12px] object-cover border border-[rgba(213,215,218,1)]" />
                <LoadingOverlay className='bg-white/50' loading={detailLoading && triggerId === item.uuid} />
                {item.templateResource === 1 && <span className='absolute top-2 left-2 bg-black/40 text-[rgba(255,255,255,1)] text-xs font-medium rounded-[4px] px-1 py-0.5'>{t('agent_dashboard.user_detail.template.add_modal.preset')}</span>}
              </div>
              <div className='opacity-0 group-hover:opacity-100 absolute bottom-2 right-2 transition-all flex gap-1 bg-[rgba(0,0,0,0.6)] rounded-[8px] px-1 py-1'>
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<Eye className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetail(item.uuid)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.template.view_detail")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
                {/* <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<Edit3Icon className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item.uuid)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.template.edit")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                /> */}
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<TrashIcon className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.uuid)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.template.delete")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
              </div>
            </div>
            <div className="absolute bottom-1 left-[10px] w-full text-start translate-y-full">
              <div className="w-full">
                <TruncatedText text={item.templateName} className='max-w-[90%] text-base font-medium text-[rgba(16,24,40,1)]' />
              </div>
              <div className="mt-1 flex items-center">
                <TruncatedText text={item.id} className='max-w-[80%] text-sm mr-1 text-[rgba(52,64,84,1)]' />
                <CopyClipboard text={item.id} />
              </div>
            </div>
          </div>
        )) : !loading && (
          <div className=' w-full h-full relative  flex flex-col justify-center items-center col-span-full mt-3 mb-16'>
            <TemplateEmptyIcon className='w-[159px] h-[136px]' />
            <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.template.template_empty")}</span>
          </div>
        )}
      </div>
      <DeleteConfirmModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
      {detailModalOpen && (
        <TemplateDetailModal<LarkImageTableItemsData>
          actors={actors}
          voices={voices}
          template={templateDetail}
          loading={detailLoading}
          open={detailModalOpen}
          deleteTemplate={deleteTemplate}
          deleteLoading={deleteLoading}
          onOpenChange={(value) => {
            setDetailModalOpen(value);
            if (!value) resetDetail();
          }}
        />
      )}
    </div>
  )
}
